import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ELEMENTS, ELEMENT_COLORS, typeMultiplier, type Element } from "@/lib/pals-data";
import { breadcrumbSchema, datasetSchema, toolSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { SNIPPETS } from "@/lib/snippet-copy";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

const FAQ: FaqItem[] = [
  { q: "Palworld elemental type weakness damage chart?", a: "The chart shows each attack element against each defender element. Green cells are strong matchups, red cells are resisted matchups and neutral cells use the normal damage multiplier." },
  { q: "Palworld elemental type weakness damage calculator?", a: "Choose the attacker element, defender element, base damage, critical state and resistance percentage to calculate the final hit value." },
  { q: "How much damage does Fire do to Grass Pals Palworld?", a: "Fire attacks are strong into Grass Pals in this calculator, so a Fire move applies the type advantage before critical and resistance modifiers." },
  { q: "Is the Pal World type chart the same as the Palworld type chart?", a: "Yes. Pal World and Palworld are the same game, so this single type chart covers both spellings: nine elements, each attack element scored against each defending element, with the exact multiplier shown per cell." },
  { q: "Which element is strongest in the Palworld type chart?", a: "No element is strongest overall — every element is strong into one element and resisted by another, so the chart is used to pick a counter per fight. Dragon and Dark cover the widest late-game boss list, while Neutral has no advantages at all." },
];

export const Route = createFileRoute("/elements")({
  head: () => ({
    meta: [
      // Deliberately does NOT use "damage calculator": that head term belongs to
      // /damage. This page owns the type-chart / weakness intent so the two
      // pages stop competing for the same query.
      { title: "Palworld Type Chart: Every Element Weakness" },
      { name: "description", content: "Palworld elemental type weakness chart for Fire, Grass, Water, Electric, Ice, Ground, Dark and Dragon, with a live multiplier lookup per matchup." },
      { property: "og:title", content: "Elemental Type Chart & Calculator" },
      { property: "og:description", content: "Live Palworld damage calculator with full type matchups." },
        { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/elements" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Elemental Type Chart & Calculator" },
        { name: "twitter:description", content: "Live Palworld damage calculator with full type matchups." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/elements" }],
    scripts: [faqSchema(FAQ, "https://palstars.com/elements"), breadcrumbSchema([["Home", "/"], ["Element Chart", "/elements"]]), toolSchema({ name: "Palworld Elemental Type Chart", description: "Full Palworld elemental effectiveness chart with a live damage multiplier calculator.", path: "/elements" }), datasetSchema({ name: "Palworld elemental effectiveness dataset", description: "Attack-element against defend-element damage multipliers for all nine Palworld elements.", path: "/elements", keywords: ["Palworld type chart", "Palworld damage multiplier", "Palworld element weakness"], variables: ["Attack element", "Defend element", "Damage multiplier"] }), snippetAnswerSchema({ question: SNIPPETS.elements.question, answer: SNIPPETS.elements.answer, path: "/elements" })],
  }),
  component: ElementsPage,
});

function ElementsPage() {
  const [atk, setAtk] = useState<Element>("Fire");
  const [def, setDef] = useState<Element>("Grass");
  const [base, setBase] = useState(120);
  const [crit, setCrit] = useState(false);
  const [resist, setResist] = useState(0);

  const mult = typeMultiplier(atk, def);
  const critMult = crit ? 1.5 : 1;
  const resistMult = 1 - resist / 100;
  const hit = Math.round(base * mult * critMult * resistMult);
  const dot = Math.round(hit * 0.15);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Elemental Chart & Damage Calculator</h1>
      <p className="aeo-answer text-muted-foreground mt-2 max-w-3xl">
        Every Palworld element matchup in one chart. Green = super effective (×2), red = resisted (×0.5). Use the calculator below to model any hit.
      </p>

      <SnippetFunnel className="mt-6" {...SNIPPETS.elements} />
      <ContextVisualStrip variant="elements" />

      <div className="glass rounded-xl p-4 mt-8 overflow-x-auto">
        <table className="w-full text-xs min-w-[600px]">
          <thead>
            <tr>
              <th className="p-2 text-left text-muted-foreground">ATK ↓ / DEF →</th>
              {ELEMENTS.map((e) => (
                <th key={e} className="p-2" style={{ color: ELEMENT_COLORS[e] }}>{e}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ELEMENTS.map((a) => (
              <tr key={a} className="border-t">
                <th className="p-2 text-left" style={{ color: ELEMENT_COLORS[a] }}>{a}</th>
                {ELEMENTS.map((d) => {
                  const m = typeMultiplier(a, d);
                  const bg = m > 1 ? "rgba(34,197,94,0.15)" : m < 1 ? "rgba(239,68,68,0.15)" : "transparent";
                  const c = m > 1 ? "#22C55E" : m < 1 ? "#EF4444" : "#94A3B8";
                  return <td key={d} className="p-2 text-center font-semibold" style={{ background: bg, color: c }}>×{m}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mt-8">
        <div className="glass rounded-xl p-6 space-y-4">
          <h2 className="font-semibold">Damage inputs</h2>
          <label className="block text-sm">
            <span className="text-muted-foreground">Attacker element</span>
            <select value={atk} onChange={(e) => setAtk(e.target.value as Element)} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2">
              {ELEMENTS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground">Defender element</span>
            <select value={def} onChange={(e) => setDef(e.target.value as Element)} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2">
              {ELEMENTS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground">Base damage</span>
            <input type="number" value={base} onChange={(e) => setBase(+e.target.value || 0)} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2" />
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground">Defender resistance %</span>
            <input type="range" min={0} max={80} value={resist} onChange={(e) => setResist(+e.target.value)} className="w-full" />
            <span className="text-xs">{resist}%</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={crit} onChange={(e) => setCrit(e.target.checked)} />
            Critical hit (×1.5)
          </label>
        </div>

        <div className="glass rounded-xl p-6 flex flex-col justify-center">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Final hit</div>
          <div className="text-6xl font-black gradient-text mt-1">{hit}</div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border p-3 bg-background/40">
              <div className="text-muted-foreground text-xs">Type multiplier</div>
              <div className="font-bold">×{mult}</div>
            </div>
            <div className="rounded-lg border p-3 bg-background/40">
              <div className="text-muted-foreground text-xs">DoT / tick</div>
              <div className="font-bold">{dot}</div>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Formula: base × type × crit × (1 − resist). DoT models a 15% bleed over-time per second.
          </p>
        </div>
      </div>
      <CrossCategoryLinks side="game" />

      <FaqSection items={FAQ} heading="Palworld element weakness FAQ" />
    </div>
  );
}
